import {
  AcceptMarketing,
  BillingFrequency,
  BillingMethod,
  type Channel,
  type Coverage,
  type OfferViewModel,
} from "@/api_client";

import { getEcommerceCheckoutOption } from "./data/ecommerce";
import insuranceInfo from "./data/insuranceInfo";
import objectInfo from "./data/objectInfo";
import processInfo from "./data/processInfo";
import productInfo from "./data/productInfo";
import type { CheckoutOptionEvent } from "./types/types";

export interface CheckoutOptionEventParams {
  contextId: string;
  offer: OfferViewModel;
  channel: Channel;
  existingCustomer: boolean;
  coverageContent: Record<string, Coverage> | undefined;
  acceptEmailMarketing?: AcceptMarketing | null;
  billingMethod?: BillingMethod | null;
  billingFrequency?: BillingFrequency | null;
}

export const createCheckoutOptionEvent = ({
  contextId,
  offer,
  channel,
  existingCustomer,
  coverageContent,
  acceptEmailMarketing,
  billingMethod,
  billingFrequency,
}: CheckoutOptionEventParams): CheckoutOptionEvent | undefined => {
  if (!offer?.quotes) {
    return;
  }
  const committedQuotes = offer.quotes.filter((q) => !!q.committedAt);
  const productInfoList = committedQuotes.map((product) =>
    productInfo.getProductInfo(product, channel),
  );
  const insuranceInfoList = committedQuotes.map((product) =>
    insuranceInfo.getInsuranceInfo(contextId, product, coverageContent),
  );
  const objectInfoList = committedQuotes.map((product) =>
    objectInfo.getObjectInfo(
      offer.enigmatized?.policyHolder,
      product,
      product.id && offer.enigmatized?.quotes
        ? offer.enigmatized.quotes[product.id]
        : undefined,
    ),
  );

  const option = existingCustomer
    ? getExistingCustomerOptions(acceptEmailMarketing)
    : getNewCustomerOptions(
        acceptEmailMarketing,
        billingMethod,
        billingFrequency,
      );
  return {
    event: "Checkout Option",
    productInfo: productInfoList,
    insuranceInfo: insuranceInfoList,
    processInfo: processInfo.getProcessInfo("checkout"),
    ecommerce: getEcommerceCheckoutOption(
      offer.quotes,
      channel,
      option,
      coverageContent,
    ),
    objectInfo: objectInfoList,
  };
};

const getExistingCustomerOptions = (
  acceptEmailMarketing?: AcceptMarketing | null,
) => {
  return `no-checkout-options, ${getOptionMarketingMails(
    acceptEmailMarketing,
  )}`;
};

const getNewCustomerOptions = (
  acceptEmailMarketing?: AcceptMarketing | null,
  billingMethod?: BillingMethod | null,
  billingFrequency?: BillingFrequency | null,
) => {
  return `${getBillingMethod(billingMethod)}, ${getBillingFrequency(
    billingFrequency,
  )}, ${getOptionMarketingMails(acceptEmailMarketing)}`;
};

const getOptionMarketingMails = (
  acceptEmailMarketing?: AcceptMarketing | null,
) => {
  if (acceptEmailMarketing === AcceptMarketing.YES) {
    return "marketing-yes";
  }
  return "marketing-no";
};
const getBillingMethod = (billingMethod?: BillingMethod | null) => {
  if (billingMethod === BillingMethod.PAPER_INVOICE) return "P";
  if (billingMethod === BillingMethod.MY_PAGES_INVOICE) return "O";
};

const getBillingFrequency = (billingFrequency?: BillingFrequency | null) => {
  if (billingFrequency === BillingFrequency.ANNUALY) return "payment-1";
  if (billingFrequency === BillingFrequency.SEMI_ANNUALY) return "payment-2";
  if (billingFrequency === BillingFrequency.QUARTERLY) return "payment-4";
  if (billingFrequency === BillingFrequency.MONTHLY) return "payment-12";
};
